import { useRef, useState } from "react";
import { format } from "date-fns";
import { cn } from "@/utils/cn";

export interface SoftHoldBreadcrumb {
  id: number;
  meetingId: string;
  meetingName: string;
  preferredDates: Array<{
    startDate: string;
    endDate: string;
    isItPrimaryDate: boolean;
  }>;
}

interface GRCBreadcrumbsProps {
  breadcrumbs: SoftHoldBreadcrumb[];
  onNavigate: (breadcrumb: SoftHoldBreadcrumb) => void;
}

function getTooltipContent(item: SoftHoldBreadcrumb): { meetingInfo: string; dateRange: string } {
  const meetingInfo = item.meetingId
    ? `${item.meetingId} | ${item.meetingName}`
    : item.meetingName;

  let dateRange = "";
  if (item.preferredDates.length > 0) {
    const preferredDate =
      item.preferredDates.length > 1
        ? (item.preferredDates.find((d) => d.isItPrimaryDate) ?? item.preferredDates[0])
        : item.preferredDates[0];
    if (preferredDate) {
      const start = format(new Date(preferredDate.startDate), "EEE, MM/dd/yyyy");
      const end = format(new Date(preferredDate.endDate), "EEE, MM/dd/yyyy");
      dateRange = `${start} - ${end}`;
    }
  }

  return { meetingInfo, dateRange };
}

function BreadcrumbItem({
  item,
  onNavigate
}: {
  item: SoftHoldBreadcrumb;
  onNavigate: (breadcrumb: SoftHoldBreadcrumb) => void;
}) {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const { meetingInfo, dateRange } = getTooltipContent(item);

  return (
    <span
      ref={triggerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setTooltipVisible(true)}
      onMouseLeave={() => setTooltipVisible(false)}
      onFocus={() => setTooltipVisible(true)}
      onBlur={() => setTooltipVisible(false)}
    >
      <button
        type="button"
        onClick={() => onNavigate(item)}
        className="max-w-[180px] truncate text-xs text-blue-600 underline underline-offset-2 hover:text-blue-800 hover:underline focus:outline-none focus-visible:underline"
      >
        {item.meetingName}
      </button>

      {tooltipVisible && (
        <div
          role="tooltip"
          className={cn(
            "absolute bottom-full left-0 z-50 mb-1.5 min-w-max max-w-xs",
            "rounded-none border border-slate-200 bg-white px-3 py-2 shadow-md"
          )}
        >
          <p className="text-xs font-semibold text-slate-900">{meetingInfo}</p>
          {dateRange && <p className="mt-0.5 text-xs text-slate-600">{dateRange}</p>}
          <span
            className="absolute -bottom-1 left-4 h-2 w-2 rotate-45 border-b border-r border-slate-200 bg-white"
            aria-hidden="true"
          />
        </div>
      )}
    </span>
  );
}

export function GRCBreadcrumbs({ breadcrumbs, onNavigate }: GRCBreadcrumbsProps) {
  if (breadcrumbs.length === 0) return null;

  return (
    <nav
      aria-label="Soft hold breadcrumbs"
      className="flex flex-wrap items-center gap-x-1 bg-white px-3 py-1.5"
    >
      {breadcrumbs.map((item, index) => (
        <span key={item.id} className="flex items-center gap-x-1">
          {index > 0 && (
            <span className="select-none text-xs text-slate-400" aria-hidden="true">
              /
            </span>
          )}
          <BreadcrumbItem item={item} onNavigate={onNavigate} />
        </span>
      ))}
    </nav>
  );
}
